import { Box, Stack, Typography, IconButton } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import React, { useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';

import { InputSelect } from '../elements/InputSelect';
import { SelectChangeEvent } from '@mui/material/Select';
import { SingleEntryField } from 'services/option';
import { DatePicker } from '../elements/DatePicker';
import dayjs from 'dayjs';
import { InputText } from '../elements/InputText';
import { ButtonCustom } from '../elements/ButtonCustom';

/* eslint-disable @typescript-eslint/no-explicit-any */
export const combineSingleValues = (obj: Record<string, any>) => {
  const singles: { index: number; value: string }[] = [];

  for (const key in obj) {
    if (!key.startsWith('single_')) continue;

    const rawValue = obj[key];
    if (!rawValue) continue;

    const index = Number(key.slice(7));

    // Detect date or keep text
    const formattedValue = dayjs(rawValue).isValid()
      ? dayjs(rawValue).format('YYYY/MM/DD')
      : rawValue;

    singles.push({ index, value: formattedValue });
  }

  singles.sort((a, b) => a.index - b.index);

  return singles.map((item) => item.value).join(' - ');
};

type AdditionalStructureProps = {
  index: number;
  additionalStructureOptionSingleField: SingleEntryField[];
  remove: () => void;
  titleOptions: { label: string; value: string }[];
  onRemovePhoto?: (photoIndex: number) => void;
};

export const AdditionalStructureItem = ({
  index,
  remove,
  additionalStructureOptionSingleField,
  titleOptions
}: AdditionalStructureProps) => {
  const { control, watch, setValue } = useFormContext();
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const [structureFields, setStructureFields] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<string>('');
  const [step1Done, setStep1Done] = useState(false);
  const handlePickPhotos = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length) {
      const existing = watch(`locationDetail.additionalStructure.${index}.photos`) || [];
      const updated = [...existing, ...files];
      setValue(`locationDetail.additionalStructure.${index}.photos`, updated);
    }
  };
  return (
    <Box
      sx={{
        width: '100%',
        background: '#F5F9FC',
        borderRadius: 2,
        padding: 2,
        mb: 2,
        position: 'relative'
      }}>
      {/* STEP 1 — CHỌN TITLE */}
      {!step1Done && (
        <Stack flexDirection={'row'} gap={'20px'} alignItems={'flex-start'}>
          <Stack width={'100%'} gap={'16px'} alignItems={'flex-end'}>
            <Stack width={'100%'} gap={'16px'}>
              <InputSelect
                title="Select Structure Title"
                value={selected}
                placeholder="Select Structure Title"
                inputError={undefined}
                onChange={(e: SelectChangeEvent<unknown>) => {
                  setSelected(e?.target?.value as string);
                }}
                options={titleOptions.map((opt) => ({
                  label: opt.label,
                  value: opt.label
                }))}
              />
              {additionalStructureOptionSingleField?.map((field) => (
                <React.Fragment key={field.id}>
                  {field.type === 'DATE' ? (
                    <DatePicker
                      title={`${field.name}:`}
                      value={
                        structureFields[`single_${field.id}`]
                          ? dayjs(structureFields[`single_${field.id}`])
                          : undefined
                      }
                      onChange={(val) =>
                        setStructureFields((prev: Record<string, string>) => ({
                          ...prev,
                          [`single_${field.id}`]: val ? dayjs(val).toISOString() : ''
                        }))
                      }
                    />
                  ) : (
                    <InputText
                      title={`${field.name}:`}
                      placeholder={`Enter ${field.name}`}
                      inputProps={{
                        value: structureFields[`single_${field.id}`] || '',
                        onChange: (event: any) => {
                          const value =
                            event && typeof event === 'object' && 'target' in event
                              ? (event.target as HTMLInputElement).value
                              : (event as string);
                          setStructureFields((prev: Record<string, string>) => ({
                            ...prev,
                            [`single_${field.id}`]: value
                          }));
                        }
                      }}
                    />
                  )}
                </React.Fragment>
              ))}
            </Stack>
            <ButtonCustom
              style={{ height: '40px', width: '160px', marginBottom: '30px' }}
              label={'Next'}
              onClick={() => {
                setValue(
                  `locationDetail.additionalStructure.${index}.title`,
                  `${selected} ${structureFields ? `- ${combineSingleValues(structureFields)}` : ''}`
                );
                setStep1Done(true);
              }}
            />
          </Stack>
          <IconButton onClick={remove}>
            <DeleteIcon />
          </IconButton>
        </Stack>
      )}

      {/* STEP 2 — UPLOAD PHOTOS */}
      {step1Done && (
        <>
          <Stack
            flexDirection={'row'}
            gap={'20px'}
            width={'100%'}
            justifyContent={'space-between'}
            alignItems={'flex-start'}
            mb={2}>
            <Typography fontWeight={600} fontSize={18}>
              {watch(`locationDetail.additionalStructure.${index}.title`)}
            </Typography>
            <Stack direction="row" justifyContent="flex-end" gap={'20px'}>
              <IconButton
                component="label"
                sx={{
                  background: '#1E6FD9',
                  color: 'white',
                  '&:hover': { background: '#1452A5' }
                }}>
                <PhotoCameraIcon />
                <input hidden type="file" accept="image/*" multiple onChange={handlePickPhotos} />
              </IconButton>
              <IconButton onClick={remove}>
                <DeleteIcon />
              </IconButton>
            </Stack>
          </Stack>

          {/* PREVIEW PHOTOS */}
          <Controller
            name={`locationDetail.additionalStructure.${index}.photos`}
            control={control}
            render={({ field }) => (
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {field.value?.map((file: File, i: number) => (
                  <Box
                    key={i}
                    sx={{
                      width: 90,
                      height: 90,
                      borderRadius: 10,
                      position: 'relative'
                    }}>
                    <img
                      src={URL.createObjectURL(file)}
                      style={{
                        width: '100%',

                        borderRadius: 10,
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => {
                        const updated = field.value.filter((_: any, idx: number) => idx !== i);
                        setValue(`locationDetail.additionalStructure.${index}.photos`, updated);
                      }}
                      sx={{
                        position: 'absolute',
                        top: -10,
                        right: -10,
                        background: 'rgba(0,0,0,0.5)',
                        color: 'white',
                        '&:hover': { background: 'rgba(0,0,0,0.7)' }
                      }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            )}
          />
        </>
      )}
    </Box>
  );
};
